import { ROUTES } from "@/shared/constants";
import { useAuth } from "@/shared/stores/useAuth";
import { useNotificationStore } from "@/shared/stores/useNotificationStore";
import { useCurrentTalentProfile } from "@/shared/stores/useProfile";
import {
  getTalentSocketNotification,
  getUserSocketNotification,
} from "@/shared/utils/notificationHandler";
import { playNotificationSound } from "@/shared/utils/notificationSound";
import SocketHandler from "@/shared/utils/socketHandler";
import { isFlutterEmbeddedMobileViewport } from "@/shared/utils/taasEmbeddedContext";
import { Box, CssBaseline, useMediaQuery, useTheme } from "@mui/material";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MobileFooterView from "../footer/MobileFooterView";
import { Header } from "../header";
import TalentSidebar from "../sidebar/TalentSidebar";
import EmbeddedMoreSheet from "./EmbeddedMoreSheet";
import LayoutMain from "./LayoutMain";
import { useLaunchStore } from "./useLaunchStore";

const TalentLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Closed on mobile so we don't open then immediately dismiss on first paint.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentProfile } = useCurrentTalentProfile();

  useLayoutEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  const toggleDrawer = () => setDrawerOpen((prev) => !prev);

  const pathSegments = location.pathname.split("/").filter(Boolean);

  const buildPath = (index: number) => "/" + pathSegments.slice(0, index + 1).join("/");

  const [badgeVisible, setBadgeVisible] = useState(false);

  const [socketNotification, setSocketNotification] = useState<any>([]);
  const { userInfo } = useAuth();
  const { setCurrentNotification } = useNotificationStore();
  const socketSubscribedRef = useRef(false);
  useEffect(() => {
    // Prevent multiple subscriptions
    if (socketSubscribedRef.current || !userInfo?.id) {
      return;
    }

    try {
      // Get socket instance, open connection with auth token, then subscribe
      const socketHandler = SocketHandler.getInstance();
      socketHandler.connect();

      socketHandler.subscribeToNotification(userInfo.id.toString(), (message) => {
        if (message) {
          const notification = currentProfile?.id
            ? getTalentSocketNotification(message, currentProfile?.id)
            : getUserSocketNotification(message);
          console.log("notification are here in main layout", notification);
          if (notification) {
            setSocketNotification((prev: any) => [...prev, notification]);
            setBadgeVisible(true);
            setCurrentNotification(notification);
            // Play notification sound
            playNotificationSound();
          }
        }
        // Handle the message here
      });

      socketSubscribedRef.current = true;
      console.log("Successfully subscribed to notification socket");
    } catch (error) {
      console.error("Failed to subscribe to socket:", error);
    }

    // Cleanup subscription when component unmounts
    return () => {
      try {
        if (userInfo?.id) {
          const socketHandler = SocketHandler.getInstance();
          socketHandler.unsubscribeFromNotification(userInfo.id.toString());
          socketSubscribedRef.current = false;
          console.log("Successfully unsubscribed from notification socket");
        }
      } catch (error) {
        console.error("Failed to unsubscribe from socket:", error);
      }
    };
  }, [userInfo?.id, currentProfile?.id]);

  const showEmbeddedBottomNav = isFlutterEmbeddedMobileViewport(isMobile) || isMobile;

  return (
    <Box sx={{ display: "flex", bgcolor: "var(--page-bg)", minHeight: "100dvh" }}>
      <CssBaseline />

      <GlobalLaunchOverlay />

      {/* Header and Sidebar */}
      <Header
        toggleDrawer={toggleDrawer}
        setBadgeVisible={setBadgeVisible}
        badgeVisible={badgeVisible}
        socketNotification={socketNotification}
      />
      <TalentSidebar
        open={drawerOpen}
        handleClose={() => setDrawerOpen(false)}
        toggleDrawer={toggleDrawer}
      />

      <LayoutMain showBottomNav={showEmbeddedBottomNav} />

      {showEmbeddedBottomNav && <MobileFooterView variant="talent" />}
      {/* {showMobileFooter && <MobileFooterView variant="talent" />} */}

      <EmbeddedMoreSheet
        open={moreSheetOpen}
        onClose={() => setMoreSheetOpen(false)}
        actions={[
          {
            key: "notifications",
            label: "Notifications",
            onClick: () => navigate(ROUTES.NOTIFICATION),
          },
          {
            key: "settings",
            label: "Settings",
            onClick: () => navigate(ROUTES.SETTINGS),
          },
        ]}
      />
    </Box>
  );
};

// ── Global launch overlay — rendered ABOVE the Outlet so it survives navigation ─
const GlobalLaunchOverlay: React.FC = () => {
  const { payload, clear } = useLaunchStore();
  const [expanded, setExpanded] = useState(false);
  const prevPayloadRef = useRef<typeof payload>(null);

  useEffect(() => {
    if (!payload) {
      setExpanded(false);
      return;
    }
    // Only kick off the expansion when a new payload arrives
    if (payload !== prevPayloadRef.current) {
      prevPayloadRef.current = payload;
      setExpanded(false);
      let id1: number, id2: number;
      id1 = requestAnimationFrame(() => {
        id2 = requestAnimationFrame(() => setExpanded(true));
      });
      // Auto-clear after animation + navigation settle
      const t = setTimeout(() => clear(), 500);
      return () => {
        cancelAnimationFrame(id1);
        cancelAnimationFrame(id2);
        clearTimeout(t);
      };
    }
  }, [payload, clear]);

  if (!payload) return null;

  const { bg, glowColor, iconColor, icon, originX, originY, startRadius } = payload;
  const endR = Math.hypot(window.innerWidth, window.innerHeight) + 50;
  const ease = "cubic-bezier(0.32, 0.72, 0, 1)";
  const dur = "0.46s";

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: bg,
        pointerEvents: "none",
        clipPath: expanded
          ? `circle(${endR}px at ${originX}px ${originY}px)`
          : `circle(${startRadius}px at ${originX}px ${originY}px)`,
        transition: expanded ? `clip-path ${dur} ${ease}` : "none",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: "linear-gradient(140deg, rgba(255,255,255,0.20) 0%, transparent 40%)",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: originY,
          left: originX,
          transform: expanded ? "translate(-50%,-50%) scale(3.5)" : "translate(-50%,-50%) scale(1)",
          opacity: expanded ? 0 : 1,
          transition: expanded ? `transform ${dur} ${ease}, opacity 0.25s ease` : "none",
          color: iconColor,
          filter: `drop-shadow(0 0 20px ${glowColor})`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
    </Box>
  );
};

export default TalentLayout;
